// Play hub. Sits between the marketing home page and the actual app
// surfaces (tracks, roster, race). Three big image tiles act as
// launchers; the Race tile holds a track dropdown so the user can
// pick a circuit before committing to race-setup.
//
// Reuses the same fixed nav, fonts, and visual language as the home
// page — `body.home-route` is set on mount so the global topbar
// hides and the page can take the full viewport.

import { listTracks, setSelectedTrack } from '../api.js';

export async function render(mountEl) {
  document.body.classList.add('home-route');

  // Initial markup with placeholder for the track dropdown — we
  // populate it after listTracks() resolves so a slow demo fixture
  // doesn't block paint.
  mountEl.innerHTML = `
    <div class="home-root play-root">
      <nav class="home-nav">
        <span class="home-brand">Apex Slayer <span class="sub">// Race Engineer</span></span>
        <div class="home-nav-spacer"></div>
        <a href="#/" class="home-nav-link">Home</a>
        <a href="#/tracks" class="home-nav-link">Tracks</a>
        <a href="#/roster" class="home-nav-link">Roster</a>
        <a href="https://discord.gg/CFrXRATXY8" class="home-nav-link discord" target="_blank" rel="noopener noreferrer">Discord</a>
      </nav>

      <section class="play-hero">
        <div class="home-section-label">// Pit lane</div>
        <h1 class="play-h1">Pick your <span class="accent">line.</span></h1>
        <p class="play-subtitle">Three doors. Build a circuit, set up the grid, or call the race.</p>
      </section>

      <section class="play-tiles">
        <a class="play-tile" href="#/tracks" data-tile="tracks">
          <div class="play-tile-img">
            <img src="./homepage-images/BrandsHatchEditScreen.jpg" alt="Track editor showing Brands Hatch with racing line and pit lane" onerror="this.style.display='none'">
          </div>
          <div class="play-tile-body">
            <div class="play-tile-num">/ 01</div>
            <div class="play-tile-title">Tracks</div>
            <div class="play-tile-desc">Build a circuit from a satellite image. Trace the line, lay the kerbs, set the pit lane.</div>
            <div class="play-tile-cta">Open tracks <span class="arrow">→</span></div>
          </div>
        </a>

        <a class="play-tile" href="#/roster" data-tile="roster">
          <div class="play-tile-img">
            <img src="./homepage-images/DriversEditScreen.jpg" alt="Roster editor showing driver list and attributes" onerror="this.style.display='none'">
          </div>
          <div class="play-tile-body">
            <div class="play-tile-num">/ 02</div>
            <div class="play-tile-title">Roster</div>
            <div class="play-tile-desc">Drivers, teams, cars, and series. Edit attributes, build a lineup.</div>
            <div class="play-tile-cta">Manage roster <span class="arrow">→</span></div>
          </div>
        </a>

        <div class="play-tile play-tile-race" data-tile="race">
          <div class="play-tile-img">
            <img src="./homepage-images/BrandsHatchRace.jpg" alt="Apex Slayer race in progress on Brands Hatch" onerror="this.style.display='none'">
          </div>
          <div class="play-tile-body">
            <div class="play-tile-num">/ 03</div>
            <div class="play-tile-title">Race</div>
            <div class="play-tile-desc">Pick a circuit, set the grid, run the race.</div>

            <div class="play-tile-form">
              <label class="play-tile-label" for="play-race-track">Track</label>
              <select id="play-race-track" class="play-select" disabled>
                <option>Loading tracks…</option>
              </select>
              <button id="play-race-go" class="home-btn play-tile-go" disabled>
                Go to grid <span class="arrow">→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <div class="play-spacer"></div>
    </div>
  `;

  const selectEl = mountEl.querySelector('#play-race-track');
  const goBtn    = mountEl.querySelector('#play-race-go');

  const onGo = () => {
    const trackId = selectEl.value;
    if (!trackId) return;
    setSelectedTrack(trackId);
    window.location.hash = '#/race-setup';
  };
  goBtn.addEventListener('click', onGo);

  // Enter on the dropdown also commits — common keyboard expectation.
  const onKeydown = (e) => {
    if (e.key === 'Enter' && !goBtn.disabled) {
      e.preventDefault();
      onGo();
    }
  };
  selectEl.addEventListener('keydown', onKeydown);

  try {
    const tracks = await listTracks();
    if (!tracks.length) {
      selectEl.innerHTML = `<option value="">No tracks yet</option>`;
      goBtn.textContent = 'Build one first →';
      goBtn.disabled = true;
      return;
    }
    selectEl.innerHTML = tracks
      .map(t => `<option value="${escapeAttr(t.id)}">${escapeHtml(t.name)}</option>`)
      .join('');
    selectEl.disabled = false;
    goBtn.disabled = false;
  } catch (err) {
    selectEl.innerHTML = `<option value="">Couldn't load tracks</option>`;
    goBtn.disabled = true;
    console.error('listTracks() failed:', err);
  }

  return () => {
    goBtn.removeEventListener('click', onGo);
    selectEl.removeEventListener('keydown', onKeydown);
    document.body.classList.remove('home-route');
  };
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function escapeAttr(s) { return escapeHtml(s); }
